import { useCallback } from 'react';
import { useWidgetHost } from '@/core/widgets/export';

export type SearchActionType = 'share' | 'bookmark' | 'pin' | 'expand' | 'open';

interface SearchActionPermissions {
  canShare: boolean;
  canBookmark: boolean;
  canPin: boolean;
  canExpand: boolean;
  canOpen: boolean;
}

const WIDGET_ACTION_CONFIG: Record<string, SearchActionPermissions> = {
  tabs: {
    canShare: false,
    canBookmark: true,
    canPin: true,
    canExpand: true,
    canOpen: true,
  },
  dashboard: {
    canShare: true,
    canBookmark: true,
    canPin: true,
    canExpand: true,
    canOpen: true,
  },
  // Default configuration for any other widget host
  default: {
    canShare: true,
    canBookmark: true,
    canPin: true,
    canExpand: true,
    canOpen: true,
  },
};

export function useSearchActionPermissions() {
  const widgetHost = useWidgetHost();

  const canPerformAction = useCallback(
    (action: SearchActionType): boolean => {
      const config = WIDGET_ACTION_CONFIG[widgetHost.hostId] || WIDGET_ACTION_CONFIG.default;

      switch (action) {
        case 'share':
          return config.canShare;
        case 'bookmark':
          return config.canBookmark;
        case 'pin':
          return config.canPin;
        case 'expand':
          return config.canExpand;
        case 'open':
          return config.canOpen;
        default:
          return true;
      }
    },
    [widgetHost.hostId],
  );

  const getPermissions = useCallback((): SearchActionPermissions => {
    return WIDGET_ACTION_CONFIG[widgetHost.hostId] || WIDGET_ACTION_CONFIG.default;
  }, [widgetHost.hostId]);

  return {
    canPerformAction,
    permissions: getPermissions(),
  };
}
